import React, { useState, useEffect } from 'react';
import { X, FileText, CreditCard, Loader2, Upload, Image as ImageIcon, File, XCircle, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { admissionsService } from '../../firebase/admissions';
import { paymentManagementService } from '../../firebase/paymentManagement';
import { useRealtime } from '../../contexts/RealtimeContext';
import SmartPaymentModal from '../payment/SmartPaymentModal';
import { uploadFile } from '../../firebase/r2storage';
import { generateApplicationReceipt } from '../../utils/receiptGenerator';
import { 
  checkRecipientPayoutStatus, 
  initializeRouteCheckout, 
  calculateBreakdown,
  PLATFORM_FEE_PERCENTAGE,
  RECIPIENT_PERCENTAGE
} from '../../utils/routePaymentHelper';
import toast from 'react-hot-toast';

const ApplicationForm = ({ institution, onClose, onSuccess, testRegistration }) => {
  const { currentUser } = useRealtime();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [curriculum, setCurriculum] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [institutionPayoutStatus, setInstitutionPayoutStatus] = useState(null);
  const [institutionOwnerId, setInstitutionOwnerId] = useState(null);
  const [applicationData, setApplicationData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    email: currentUser?.email || '',
    phone: '',
    
    // Address
    address: {
      street: '',
      city: '',
      state: '',
      pincode: ''
    },
    
    // Academic Information
    previousSchool: '',
    previousClass: '',
    applyingForClass: '',
    applyingForCourse: '', // For coachings and colleges
    selectedCourseId: '', // Store selected course ID
    academicYear: new Date().getFullYear() + 1,
    
    // Additional Information
    parentName: '',
    parentPhone: '',
    parentEmail: '',
    parentOccupation: '',
    
    // Custom fields data
    customFieldsData: {},
    
    // Documents (will be handled separately)
    documents: []
  });

  const [errors, setErrors] = useState({});

  // Load custom fields from institution customization
  useEffect(() => {
    loadCustomFields();
  }, [institution]);

  // Load curriculum for coachings and colleges
  useEffect(() => {
    if (institution && (institution.type === 'coaching' || institution.type === 'college')) {
      loadCurriculum();
    }
    
    // Find institution owner and check if they can receive Route payments
    const findOwnerAndCheckPayout = async () => {
      const institutionId = institution?.id;
      if (!institutionId) return;
      
      try {
        // Find the coaching admin (owner) who has the linked Razorpay account
        const ownerQuery = query(
          collection(db, 'users'),
          where('coachingId', '==', institutionId),
          where('role', '==', 'coaching_admin'),
          limit(1)
        );
        const ownerSnapshot = await getDocs(ownerQuery);
        
        if (!ownerSnapshot.empty) {
          const ownerId = ownerSnapshot.docs[0].id;
          setInstitutionOwnerId(ownerId);
          
          // Check if owner can receive Route payments
          const status = await checkRecipientPayoutStatus(ownerId);
          setInstitutionPayoutStatus(status);
          console.log('Institution owner payout status:', { ownerId, status });
        }
      } catch (error) {
        console.log('Could not find institution owner:', error);
      }
    };
    
    findOwnerAndCheckPayout();
  }, [institution]);

  const loadCustomFields = async () => {
    try {
      const institutionId = institution.id;
      const result = await admissionsService.getInstitutionCustomization(institutionId);
      if (result.success && result.customization?.applicationSettings?.customFields) {
        setCustomFields(result.customization.applicationSettings.customFields);
      }
    } catch (error) {
    }
  };

  const loadCurriculum = async () => {
    try {
      const institutionId = institution.id;
      const result = await admissionsService.getInstitutionCurriculum(institutionId);
      if (result.success) {
        setCurriculum(result.curriculum || []);
      }
    } catch (error) {
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!applicationData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!applicationData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!applicationData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!applicationData.gender) newErrors.gender = 'Gender is required';
    if (!applicationData.email.trim()) newErrors.email = 'Email is required';
    if (!applicationData.phone.trim()) newErrors.phone = 'Phone is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!applicationData.address.street.trim()) newErrors.street = 'Street address is required';
    if (!applicationData.address.city.trim()) newErrors.city = 'City is required';
    if (!applicationData.address.state.trim()) newErrors.state = 'State is required';
    if (!applicationData.address.pincode.trim()) newErrors.pincode = 'Pincode is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    
    if (!applicationData.previousSchool.trim()) newErrors.previousSchool = 'Previous school is required';
    if (!applicationData.previousClass.trim()) newErrors.previousClass = 'Previous class is required';
    
    // For schools: validate applyingForClass
    // For coachings and colleges: validate applyingForCourse
    if (institution.type === 'school') {
      if (!applicationData.applyingForClass.trim()) {
        newErrors.applyingForClass = 'Applying for class is required';
      }
    } else if (institution.type === 'coaching' || institution.type === 'college') {
      if (!applicationData.selectedCourseId) {
        newErrors.applyingForCourse = 'Please select a course';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCourseSelection = (courseId) => {
    const course = curriculum.find(c => c.id === courseId);
    if (course) {
      setSelectedCourse(course);
      setApplicationData(prev => ({
        ...prev,
        selectedCourseId: courseId,
        applyingForCourse: course.title
      }));
    }
  };

  const parseFees = (feesString) => {
    if (!feesString) return 0;
    // Handle different fee formats
    const str = feesString.toString().trim();
    
    // If it's already a number, return it
    if (!isNaN(str) && !str.includes(',')) {
      return parseFloat(str);
    }
    
    // Extract number from strings like "₹50,000/year", "50000", "₹50000", "50,000"
    // Remove currency symbols, text, and extract numbers
    const cleaned = str.replace(/[₹$€£]/g, '').replace(/[^\d,.-]/g, '');
    const match = cleaned.match(/[\d,]+/);
    if (match) {
      const numberStr = match[0].replace(/,/g, '');
      const parsed = parseFloat(numberStr);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const validateStep4 = () => {
    const newErrors = {};
    
    if (!applicationData.parentName.trim()) newErrors.parentName = 'Parent name is required';
    if (!applicationData.parentPhone.trim()) newErrors.parentPhone = 'Parent phone is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep5 = () => {
    const newErrors = {};
    
    // Validate custom fields
    customFields.forEach(field => {
      if (field.required) {
        const fieldValue = applicationData.customFieldsData?.[field.label];
        if (!fieldValue || (!fieldValue.value && !fieldValue.url)) {
          newErrors[`custom_${field.label}`] = `${field.label} is required`;
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setApplicationData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setApplicationData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error for this field
    if (errors[field] || errors[field.split('.')[1]]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (field.includes('.')) {
          delete newErrors[field.split('.')[1]];
        } else {
          delete newErrors[field];
        }
        return newErrors;
      });
    }
  };

  const handleFileUpload = async (fieldLabel, file, fieldType) => {
    if (!file) return;

    // Validate file
    const maxSize = fieldType === 'photo' ? 5 * 1024 * 1024 : 10 * 1024 * 1024; // 5MB for photos, 10MB for files
    if (file.size > maxSize) {
      toast.error(`File size must be less than ${fieldType === 'photo' ? '5MB' : '10MB'}`);
      return;
    }

    if (fieldType === 'photo') {
      // Validate image type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
    }

    try {
      setUploadingFiles(prev => ({ ...prev, [fieldLabel]: true }));
      
      // Create storage path
      const fileName = `${Date.now()}_${file.name}`;
      const storagePath = `admissions/applications/${institution.id}/${currentUser.uid}/${fileName}`;

      // Upload file to R2
      const result = await uploadFile(file, storagePath);
      
      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }

      // Store in application data
      setApplicationData(prev => ({
        ...prev,
        customFieldsData: {
          ...prev.customFieldsData,
          [fieldLabel]: {
            url: result.url,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            uploadedAt: new Date().toISOString()
          }
        }
      }));

      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file: ' + error.message);
    } finally {
      setUploadingFiles(prev => ({ ...prev, [fieldLabel]: false }));
    }
  };

  const removeFile = (fieldLabel) => {
    setApplicationData(prev => {
      const newData = { ...prev };
      delete newData.customFieldsData[fieldLabel];
      return newData;
    });
  };

  const handleNext = async () => {
    let isValid = false;
    
    switch (step) {
      case 1:
        isValid = validateStep1();
        break;
      case 2:
        isValid = validateStep2();
        break;
      case 3:
        isValid = validateStep3();
        break;
      case 4:
        isValid = validateStep4();
        break;
      case 5:
        isValid = validateStep5();
        break;
      default:
        isValid = true;
    }
    
    if (isValid) {
      if (step === 5) {
        // For coachings/colleges, ensure a course is selected before payment
        if ((institution.type === 'coaching' || institution.type === 'college') && !selectedCourse) {
          toast.error('Please select a course to proceed');
          return;
        }
        // Ensure fee is valid
        if (applicationFee <= 0 && (institution.type === 'coaching' || institution.type === 'college')) {
          toast.error('Please select a course with a valid fee');
          return;
        }
        
        // Check if institution can receive Route payments
        if (institutionPayoutStatus?.canReceive && institutionOwnerId) {
          // Use Razorpay Route for instant transfer to institution
          const breakdown = calculateBreakdown(applicationFee, 'application_fee');
          
          try {
            setLoading(true);
            
            await initializeRouteCheckout({
              amount: applicationFee,
              recipientUserId: institutionOwnerId,
              paymentType: 'application_fee',
              payerDetails: {
                name: `${applicationData.firstName} ${applicationData.lastName}`,
                email: applicationData.email || currentUser?.email,
                phone: applicationData.phone || ''
              },
              notes: {
                institutionId: institution.id,
                institutionName: institution.name,
                applicantName: `${applicationData.firstName} ${applicationData.lastName}`
              },
              razorpayKeyId: process.env.REACT_APP_RAZORPAY_KEY_ID,
              onSuccess: async (response) => {
                await handleRoutePaymentSuccess(response, breakdown);
              },
              onFailure: (error) => {
                setLoading(false);
                toast.error(error.error || 'Payment failed. Please try again.');
              },
              onDismiss: () => {
                setLoading(false);
              }
            });
          } catch (error) {
            setLoading(false);
            toast.error(error.message || 'Failed to initiate payment');
          }
        } else {
          // Fallback to regular payment modal
          setShowPayment(true);
        }
      } else {
        setStep(step + 1);
      }
    }
  };

  // Handle Route payment success
  const handleRoutePaymentSuccess = async (response, breakdown) => {
    try {
      // Save payment transaction
      const paymentResult = await paymentManagementService.savePaymentTransaction({
        userId: currentUser.uid,
        amount: applicationFee,
        currency: 'INR',
        paymentId: response.razorpay_payment_id,
        orderId: response.razorpay_order_id,
        signature: response.razorpay_signature,
        paymentType: 'admission_application',
        status: 'completed',
        isRoutePayment: true,
        platformFee: breakdown.platformFee,
        recipientAmount: breakdown.recipientAmount,
        metadata: {
          institutionId: institution.id,
          institutionName: institution.name,
          applicationData: applicationData
        }
      });

      if (!paymentResult.success) {
        throw new Error('Failed to save payment transaction');
      }

      // Save application
      const applicationResult = await admissionsService.submitApplication({
        userId: currentUser.uid,
        institutionId: institution.id,
        institutionName: institution.name,
        institutionType: institution.type,
        ...applicationData,
        customFieldsData: applicationData.customFieldsData || {},
        selectedCourseId: applicationData.selectedCourseId || null,
        selectedCourseTitle: selectedCourse?.title || null,
        selectedCourseFees: selectedCourse?.fees || null,
        paymentTransactionId: paymentResult.transactionId,
        paymentAmount: applicationFee,
        paymentStatus: 'completed',
        isRoutePayment: true,
        status: 'submitted'
      });

      if (!applicationResult.success) {
        throw new Error(applicationResult.error);
      }

      // Save admission payment
      await admissionsService.saveApplicationPayment({
        applicationId: applicationResult.applicationId,
        userId: currentUser.uid,
        institutionId: institution.id,
        transactionId: paymentResult.transactionId,
        amount: applicationFee,
        status: 'completed',
        isRoutePayment: true,
        platformFee: breakdown.platformFee,
        institutionAmount: breakdown.recipientAmount
      });

      // Generate receipt
      try {
        await generateApplicationReceipt(
          applicationData,
          institution,
          paymentResult.transactionId,
          { amount: applicationFee, status: 'completed' }
        );
        toast.success(`Application submitted! ₹${breakdown.recipientAmount} transferred to ${institution.name}.`);
      } catch (receiptError) {
        toast.success('Application submitted successfully!');
      }
      
      onSuccess();
    } catch (error) {
      toast.error('Failed to submit application: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handlePaymentSuccess = async (response, transactionData) => {
    try {
      setLoading(true);
      
      // Save payment transaction first
      const paymentResult = await paymentManagementService.savePaymentTransaction({
        userId: currentUser.uid,
        amount: applicationFee,
        currency: 'INR',
        paymentId: response.razorpay_payment_id,
        orderId: response.razorpay_order_id,
        signature: response.razorpay_signature,
        paymentType: 'admission_application',
        status: 'completed',
        metadata: {
          institutionId: institution.id,
          institutionName: institution.name,
          applicationData: applicationData
        }
      });

      if (!paymentResult.success) {
        throw new Error('Failed to save payment transaction');
      }

      // Save application
      const applicationResult = await admissionsService.submitApplication({
        userId: currentUser.uid,
        institutionId: institution.id,
        institutionName: institution.name,
        institutionType: institution.type,
        ...applicationData,
        customFieldsData: applicationData.customFieldsData || {},
        // Include course information for coachings/colleges
        selectedCourseId: applicationData.selectedCourseId || null,
        selectedCourseTitle: selectedCourse?.title || null,
        selectedCourseFees: selectedCourse?.fees || null,
        paymentTransactionId: paymentResult.transactionId,
        paymentAmount: applicationFee,
        paymentStatus: 'completed',
        status: 'submitted'
      });

      if (!applicationResult.success) {
        throw new Error(applicationResult.error);
      }

      // Save admission payment
      await admissionsService.saveApplicationPayment({
        applicationId: applicationResult.applicationId,
        userId: currentUser.uid,
        institutionId: institution.id,
        transactionId: paymentResult.transactionId,
        amount: applicationFee,
        status: 'completed'
      });

      // If user was registered for test, mark as applied
      if (testRegistration && testRegistration.id) {
        try {
          await admissionsService.markTestRegistrationAsApplied(testRegistration.id);
        } catch (testError) {
          console.error('Failed to mark test registration as applied:', testError);
          // Don't fail the whole process if this fails
        }
      }

      // Generate and download receipt
      try {
        const { generateApplicationReceipt } = await import('../../utils/receiptGenerator');
        await generateApplicationReceipt(
          applicationData,
          institution,
          paymentResult.transactionId,
          {
            amount: applicationFee,
            status: 'completed'
          }
        );
        toast.success('Application submitted successfully! Receipt downloaded.');
      } catch (receiptError) {
        toast.success('Application submitted successfully! (Receipt generation failed - please install jspdf: npm install jspdf)');
      }
      
      onSuccess();
    } catch (error) {
      toast.error('Failed to submit application: ' + error.message);
    } finally {
      setLoading(false);
      setShowPayment(false);
    }
  };

  // Calculate application fee based on selected course or default
  const getApplicationFee = () => {
    // For coachings and colleges: ONLY use course fee, never institution default
    if (institution.type === 'coaching' || institution.type === 'college') {
      if (selectedCourse && selectedCourse.fees) {
        const courseFee = parseFees(selectedCourse.fees);
        if (courseFee > 0) {
          return courseFee;
        }
      }
      // If no course selected or course has no fee, return 0 (don't use institution fee)
      return 0;
    }
    // For schools: use institution default fee
    return institution.applicationFee || 500;
  };

  const applicationFee = getApplicationFee();

  return (
    <>
      <AnimatePresence>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Application Form</h2>
                <p className="text-sm text-gray-600">{institution.name}</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="px-6 py-4 bg-gray-50 border-b">
              <div className="flex items-center justify-between">
                {[1, 2, 3, 4, 5].map((s) => (
                  <div key={s} className="flex items-center flex-1">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        s <= step ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                      }`}
                    >
                      {s}
                    </div>
                    {s < 5 && (
                      <div
                        className={`flex-1 h-1 mx-2 ${
                          s < step ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2 text-xs text-gray-600 text-center">
                {step === 1 && 'Personal Information'}
                {step === 2 && 'Address'}
                {step === 3 && 'Academic Information'}
                {step === 4 && 'Parent/Guardian'}
                {step === 5 && 'Additional Information & Payment'}
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6">
              {step === 1 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold mb-4">Personal Information</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={applicationData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          errors.firstName ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.firstName && (
                        <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={applicationData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          errors.lastName ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.lastName && (
                        <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date of Birth *
                      </label>
                      <input
                        type="date"
                        value={applicationData.dateOfBirth}
                        onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.dateOfBirth && (
                        <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gender *
                      </label>
                      <select
                        value={applicationData.gender}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          errors.gender ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.gender && (
                        <p className="text-red-500 text-xs mt-1">{errors.gender}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={applicationData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      value={applicationData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                    )}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold mb-4">Address Information</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address *
                    </label>
                    <input
                      type="text"
                      value={applicationData.address.street}
                      onChange={(e) => handleInputChange('address.street', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        errors.street ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.street && (
                      <p className="text-red-500 text-xs mt-1">{errors.street}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City *
                      </label>
                      <input
                        type="text"
                        value={applicationData.address.city}
                        onChange={(e) => handleInputChange('address.city', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          errors.city ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.city && (
                        <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State *
                      </label>
                      <input
                        type="text"
                        value={applicationData.address.state}
                        onChange={(e) => handleInputChange('address.state', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          errors.state ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.state && (
                        <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pincode *
                    </label>
                    <input
                      type="text"
                      value={applicationData.address.pincode}
                      onChange={(e) => handleInputChange('address.pincode', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        errors.pincode ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.pincode && (
                      <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>
                    )}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold mb-4">Academic Information</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Previous School *
                    </label>
                    <input
                      type="text"
                      value={applicationData.previousSchool}
                      onChange={(e) => handleInputChange('previousSchool', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        errors.previousSchool ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.previousSchool && (
                      <p className="text-red-500 text-xs mt-1">{errors.previousSchool}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Previous Class *
                      </label>
                      <input
                        type="text"
                        value={applicationData.previousClass}
                        onChange={(e) => handleInputChange('previousClass', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          errors.previousClass ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.previousClass && (
                        <p className="text-red-500 text-xs mt-1">{errors.previousClass}</p>
                      )}
                    </div>

                    {/* For Schools: Show class input */}
                    {institution.type === 'school' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Applying For Class *
                        </label>
                        <input
                          type="text"
                          value={applicationData.applyingForClass}
                          onChange={(e) => handleInputChange('applyingForClass', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg ${
                            errors.applyingForClass ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="e.g., Class 11, Class 12"
                        />
                        {errors.applyingForClass && (
                          <p className="text-red-500 text-xs mt-1">{errors.applyingForClass}</p>
                        )}
                      </div>
                    )}

                    {/* For Coachings and Colleges: Show course dropdown */}
                    {(institution.type === 'coaching' || institution.type === 'college') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Applying For Course *
                        </label>
                        {curriculum.length > 0 ? (
                          <select
                            value={applicationData.selectedCourseId}
                            onChange={(e) => handleCourseSelection(e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg ${
                              errors.applyingForCourse ? 'border-red-500' : 'border-gray-300'
                            }`}
                          >
                            <option value="">Select a course</option>
                            {curriculum.map((course) => (
                              <option key={course.id} value={course.id}>
                                {course.title} {course.fees ? `- ${course.fees}` : ''}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                            No courses available. Please contact the institution.
                          </div>
                        )}
                        {errors.applyingForCourse && (
                          <p className="text-red-500 text-xs mt-1">{errors.applyingForCourse}</p>
                        )}
                        {selectedCourse && (
                          <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm font-medium text-gray-900">{selectedCourse.title}</p>
                            {selectedCourse.description && (
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">{selectedCourse.description}</p>
                            )}
                            {selectedCourse.duration && (
                              <p className="text-xs text-gray-600">Duration: {selectedCourse.duration}</p>
                            )}
                            {selectedCourse.fees && (
                              <p className="text-xs font-semibold text-blue-600 mt-1">Fee: {selectedCourse.fees}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Academic Year
                    </label>
                    <input
                      type="number"
                      value={applicationData.academicYear}
                      onChange={(e) => handleInputChange('academicYear', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold mb-4">Parent/Guardian Information</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Parent/Guardian Name *
                    </label>
                    <input
                      type="text"
                      value={applicationData.parentName}
                      onChange={(e) => handleInputChange('parentName', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        errors.parentName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.parentName && (
                      <p className="text-red-500 text-xs mt-1">{errors.parentName}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Parent Phone *
                      </label>
                      <input
                        type="tel"
                        value={applicationData.parentPhone}
                        onChange={(e) => handleInputChange('parentPhone', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          errors.parentPhone ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.parentPhone && (
                        <p className="text-red-500 text-xs mt-1">{errors.parentPhone}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Parent Email
                      </label>
                      <input
                        type="email"
                        value={applicationData.parentEmail}
                        onChange={(e) => handleInputChange('parentEmail', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Parent Occupation
                    </label>
                    <input
                      type="text"
                      value={applicationData.parentOccupation}
                      onChange={(e) => handleInputChange('parentOccupation', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              )}

              {step === 5 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold mb-4">Additional Information & Documents</h3>
                  
                  {/* Custom Fields */}
                  {customFields.length > 0 && (
                    <div className="space-y-4">
                      {customFields.map((field, index) => {
                        const fieldValue = applicationData.customFieldsData?.[field.label];
                        const isUploading = uploadingFiles[field.label];
                        
                        return (
                          <div key={index}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {field.label} {field.required && <span className="text-red-500">*</span>}
                            </label>
                            
                            {field.type === 'photo' && (
                              <div>
                                {fieldValue ? (
                                  <div className="relative inline-block">
                                    <img
                                      src={fieldValue.url}
                                      alt={field.label}
                                      className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeFile(field.label)}
                                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 -mt-2 -mr-2"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                                    {isUploading ? (
                                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                                    ) : (
                                      <>
                                        <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                                        <span className="text-sm text-gray-600">Click to upload photo</span>
                                      </>
                                    )}
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(e) => {
                                        if (e.target.files[0]) {
                                          handleFileUpload(field.label, e.target.files[0], 'photo');
                                        }
                                      }}
                                      disabled={isUploading}
                                    />
                                  </label>
                                )}
                                {errors[`custom_${field.label}`] && (
                                  <p className="text-red-500 text-xs mt-1">{errors[`custom_${field.label}`]}</p>
                                )}
                              </div>
                            )}
                            
                            {field.type === 'file' && (
                              <div>
                                {fieldValue ? (
                                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                                    <div className="flex items-center gap-2">
                                      <File className="w-5 h-5 text-gray-400" />
                                      <div>
                                        <p className="text-sm font-medium">{fieldValue.fileName}</p>
                                        <p className="text-xs text-gray-500">
                                          {(fieldValue.fileSize / 1024).toFixed(2)} KB
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeFile(field.label)}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <XCircle className="w-5 h-5" />
                                    </button>
                                  </div>
                                ) : (
                                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                                    {isUploading ? (
                                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                                    ) : (
                                      <>
                                        <Upload className="w-8 h-8 text-gray-400 mb-2" />
                                        <span className="text-sm text-gray-600">Click to upload file</span>
                                      </>
                                    )}
                                    <input
                                      type="file"
                                      className="hidden"
                                      onChange={(e) => {
                                        if (e.target.files[0]) {
                                          handleFileUpload(field.label, e.target.files[0], 'file');
                                        }
                                      }}
                                      disabled={isUploading}
                                    />
                                  </label>
                                )}
                                {errors[`custom_${field.label}`] && (
                                  <p className="text-red-500 text-xs mt-1">{errors[`custom_${field.label}`]}</p>
                                )}
                              </div>
                            )}
                            
                            {['text', 'email', 'number', 'date'].includes(field.type) && (
                              <input
                                type={field.type}
                                value={applicationData.customFieldsData?.[field.label]?.value || ''}
                                onChange={(e) => {
                                  setApplicationData(prev => ({
                                    ...prev,
                                    customFieldsData: {
                                      ...prev.customFieldsData,
                                      [field.label]: { value: e.target.value }
                                    }
                                  }));
                                }}
                                className={`w-full px-3 py-2 border rounded-lg ${
                                  errors[`custom_${field.label}`] ? 'border-red-500' : 'border-gray-300'
                                }`}
                              />
                            )}
                            
                            {field.type === 'textarea' && (
                              <textarea
                                value={applicationData.customFieldsData?.[field.label]?.value || ''}
                                onChange={(e) => {
                                  setApplicationData(prev => ({
                                    ...prev,
                                    customFieldsData: {
                                      ...prev.customFieldsData,
                                      [field.label]: { value: e.target.value }
                                    }
                                  }));
                                }}
                                rows={4}
                                className={`w-full px-3 py-2 border rounded-lg ${
                                  errors[`custom_${field.label}`] ? 'border-red-500' : 'border-gray-300'
                                }`}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Review & Payment Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-xl font-semibold mb-4">Review & Payment</h3>
                    
                    {/* Show selected course info for coachings/colleges */}
                    {(institution.type === 'coaching' || institution.type === 'college') && selectedCourse && (
                      <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-200">
                        <h4 className="font-semibold text-gray-900 mb-2">Selected Course</h4>
                        <p className="text-sm text-gray-700 font-medium">{selectedCourse.title}</p>
                        {selectedCourse.description && (
                          <p className="text-xs text-gray-600 mt-1">{selectedCourse.description}</p>
                        )}
                        {selectedCourse.duration && (
                          <p className="text-xs text-gray-600">Duration: {selectedCourse.duration}</p>
                        )}
                      </div>
                    )}
                    
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          {(institution.type === 'coaching' || institution.type === 'college') ? 'Course Fee:' : 'Application Fee:'}
                        </span>
                        <span className="font-semibold">₹{applicationFee}</span>
                      </div>
                      {(institution.type === 'coaching' || institution.type === 'college') && selectedCourse && (
                        <div className="text-xs text-gray-500 mt-1">
                          {selectedCourse.fees ? 'Fee based on selected course' : 'Please select a course to see the fee'}
                        </div>
                      )}
                      {(institution.type === 'coaching' || institution.type === 'college') && !selectedCourse && (
                        <div className="text-xs text-red-500 mt-1">
                          Please select a course to proceed
                        </div>
                      )}
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                      <p className="text-sm text-blue-800">
                        Please review all your information before proceeding to payment. 
                        Once payment is completed, your application will be submitted.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-between">
              <button
                onClick={handleBack}
                disabled={step === 1}
                className={`px-4 py-2 rounded-lg ${
                  step === 1
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Back
              </button>
              
              <button
                onClick={handleNext}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : step === 5 ? (
                  <>
                    <CreditCard className="w-4 h-4" />
                    Proceed to Payment
                  </>
                ) : step === 4 ? (
                  'Next'
                ) : (
                  'Next'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>

      {/* Payment Modal */}
      {showPayment && (
        <SmartPaymentModal
          isOpen={showPayment}
          onClose={() => setShowPayment(false)}
          paymentData={{
            amount: applicationFee,
            description: `Application fee for ${institution.name}`,
            paymentType: 'admission_application',
            metadata: {
              institutionId: institution.id,
              institutionName: institution.name,
              applicationData: applicationData
            }
          }}
          onSuccess={handlePaymentSuccess}
          title="Pay Application Fee"
        />
      )}
    </>
  );
};

export default ApplicationForm;

